'use client'

import Loader from '@/components/common/loader'
import SearchElement from '@/components/search/search-element'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { removeEmpty } from '@/functions/remove-empty'
import { trackEvent } from '@/lib/mixpanel'
import useLanguages from '@/store/helpers/swr/use-languages'
import { SearchIcon } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

const SearchHeader = ({
  searchParams,
  games: initialGames = null,
  platforms: initialPlatforms = null,
  languages: initialLanguages = null,

  /**
   * Custom search handler.
   * - Intercepts the search execution and sends the queryParams to the custom function.
   * @type {function}
   */
  customSearch = null,

  /**
   * Flags to hide form elements, one must be hidden to display correctly
   * @type {boolean}
   */
  hidePlatforms = false,
  hideGame = false,
  hideCarClass = true,
  hideDays = false,
  hideLanguage = false,
  isSearching = false,
  textPlaceholder = 'Search communities, tracks etc...',
  linkPrefix = 'sim-racing-events',
}) => {
  const router = useRouter()

  const [searchOptions, setSearchOptions] = useState([])
  const [formParams, setFormParams] = useState(searchParams)
  const [canRunSearch, setCanRunSearch] = useState(false)

  const [searching, setSearching] = useState(isSearching)

  const [filter, setFilter] = useState({
    keyword: searchParams.keyword || '',
    platforms: searchParams.platforms
      ? searchParams.platforms.split(',')
      : ['Any'],
    game: searchParams.game || 'Any',
    days: searchParams.days ? searchParams.days.split(',') : ['Any'],
    carClasses: searchParams.carClasses
      ? searchParams.carClasses.split(',')
      : ['Any'],
  })
  const platformsLoading = false
  const platformsError = null
  const gamesLoading = false
  const gamesError = null

  const {
    languages: hookLanguages,
    isLoading: languagesLoading,
    error: languagesError,
  } = useLanguages()
  const platforms = initialPlatforms || []
  const games = initialGames || []
  const languages = initialLanguages || hookLanguages
  const hasInitialData = Boolean(
    initialPlatforms && initialGames && initialLanguages,
  )

  useEffect(() => {
    if (!platforms || !games || !languages) return

    const carClassOptions =
      filter?.game && games
        ? games
            .find((game) => game.name === filter.game)
            ?.carClasses.map((carClass) => ({
              id: carClass.name,
              label: carClass.name,
              name: carClass.name,
              nicename: carClass.name,
            })) || []
        : []

    setSearchOptions([
      {
        id: 'platform',
        label: 'Platform',
        required: true,
        options: [
          {
            name: 'Any',
            nicename: 'Any',
          },
        ].concat(platforms),
        loading: hasInitialData ? false : platformsLoading,
        error: hasInitialData ? null : platformsError,
      },
      {
        id: 'game',
        label: 'Game',
        grouped: true,
        groups: [
          { prop: 'id', compareVal: 'any', label: false },
          { prop: 'featured', compareVal: true, label: 'Popular' },
          { prop: 'featured', compareVal: false, label: 'Other Titles' },
        ],
        options: [
          {
            name: 'Any',
            featured: true,
            nicename: 'Any',
            carClasses: [],
          },
        ].concat(games),
        loading: hasInitialData ? false : gamesLoading,
        error: hasInitialData ? null : gamesError,
      },
      {
        id: 'carClass',
        label: 'Car Class',
        options: [
          { id: 'any', label: 'Any', name: 'Any', nicename: 'Any' },
        ].concat(carClassOptions),
      },
      {
        id: 'days',
        label: 'Days',
        required: true,
        options: [
          {
            name: 'Any',
            nicename: 'Any',
            longname: 'Any',
          },
          {
            name: 'mon',
            nicename: 'Monday',
            longname: 'Monday',
          },
          {
            name: 'tue',
            nicename: 'Tuesday',
            longname: 'Tuesday',
          },
          {
            name: 'wed',
            nicename: 'Wednesday',
            longname: 'Wednesday',
          },
          {
            name: 'thu',
            nicename: 'Thursday',
            longname: 'Thursday',
          },
          {
            name: 'fri',
            nicename: 'Friday',
            longname: 'Friday',
          },
          {
            name: 'sat',
            nicename: 'Saturday',
            longname: 'Saturday',
          },
          {
            name: 'sun',
            nicename: 'Sunday',
            longname: 'Sunday',
          },
        ],
      },
      {
        id: 'language',
        label: 'Language',
        required: true,
        options: languages,
        loading: hasInitialData ? false : languagesLoading,
        error: hasInitialData ? null : languagesError,
      },
    ])
  }, [
    filter,
    games,
    platforms,
    languages,
    hasInitialData,
    platformsLoading,
    gamesLoading,
    languagesLoading,
    platformsError,
    gamesError,
    languagesError,
  ])

  useEffect(() => {
    let canRun = true

    const filterChoices = removeEmpty(filter, {})

    const filterChoicesKeys = Object.keys(filterChoices)
    for (let _i = 0; _i < filterChoicesKeys.length; _i++) {
      const k = filterChoicesKeys[_i]
      const option = searchOptions.find((opt) => {
        return opt.id === k
      })
      if (
        option &&
        option.required === true &&
        option.hidden !== true &&
        !formParams[k]
      ) {
        canRun = false
        break
      }
    }
    setCanRunSearch(canRun)
  }, [filter, formParams, searchOptions])

  useEffect(() => {
    setSearching(isSearching)
  }, [isSearching])

  useEffect(() => {
    setSearching(false)
    setFormParams(searchParams)
  }, [searchParams])

  /*-------------------------------*/
  /*---------- Functions ----------*/
  /*-------------------------------*/

  //for single choice
  function setGame(key, newValue) {
    if (filter[key] === newValue) {
      setFilter({ ...filter, [key]: 'Any' })
    } else {
      setFilter({ ...filter, [key]: newValue })
    }
  }

  //for multi choice
  function updateFilter(key, newValue) {
    const newFilter = { ...filter }

    if (
      (key === 'platforms' || key === 'days' || key === 'carClasses') &&
      newValue === 'Any'
    ) {
      newFilter[key] = ['Any']
      setFilter(newFilter)

      return
    }

    if (newFilter[key].some((v) => v === newValue)) {
      newFilter[key] = newFilter[key].filter((o) => o !== newValue)
    } else {
      newFilter[key].push(newValue)
    }

    if (newFilter[key].length === 0) {
      newFilter[key] = ['Any']
    } else {
      newFilter[key] = newFilter[key].filter((o) => o !== 'Any')
    }

    setFilter(newFilter)
  }

  function submitSearchOnEnterKeyPress(event) {
    if (event.key === 'Enter') {
      search()
    }
  }

  function search() {
    if (!canRunSearch) return

    setSearching(true)

    const queryParams = removeEmpty(filter, {})

    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(
        ([key, value]) =>
          !(value === 'Any' || (Array.isArray(value) && value.includes('Any'))),
      ),
    )

    filteredParams.ts = new Date().getTime()

    delete filteredParams.name
    delete filteredParams.page

    if (searchParams.orderBy) {
      filteredParams.orderBy = searchParams.orderBy
    }

    let querySearchParams = new URLSearchParams(filteredParams)

    const queryString = querySearchParams.toString()

    // Handle custom searching
    if (typeof customSearch === 'function') {
      customSearch(filteredParams)
      return
    }

    if (queryString) {
      if (linkPrefix === 'sim-racing-liveries/search') {
        trackEvent('Livery Search', filteredParams)
      } else {
        trackEvent('Event Search', filteredParams)
      }

      router.push(`/${linkPrefix}?${queryString}`, {
        scroll: false,
      })
    }
  }

  // Reset car classes if selected game changes
  useEffect(() => {
    if (filter.game === 'Any') {
      setFilter({
        ...filter,
        carClasses: ['Any'],
      })
      return
    }

    let shouldResetCarClasses = false
    if (
      filter.game &&
      filter.carClasses &&
      filter.carClasses.length > 0 &&
      filter.carClasses[0] !== 'Any' &&
      games &&
      games.length > 0
    ) {
      const gameCarClasses = games.find(
        (g) => g.name === filter.game,
      ).carClasses
      // Check if gameCarClasses contains all from filter.carClasses
      for (const carClass of filter.carClasses) {
        if (!gameCarClasses.find((c) => c.name === carClass)) {
          shouldResetCarClasses = true
          break
        }
      }
    }
    if (shouldResetCarClasses) {
      setFilter({
        ...filter,
        carClasses: ['Any'],
      })
    }
  }, [filter.game])

  return (
    <div className="w-full">
      <div className="">
        <div className="mx-auto flex w-full flex-col gap-3 font-inter desktop-sm:grid desktop-sm:grid-cols-12 desktop-sm:justify-between desktop-sm:gap-x-8">
          {!hidePlatforms && (
            <div className="col-span-2 hidden desktop-sm:block">
              <div className="text-p-2 text-neutral-blue-200">Platform</div>
            </div>
          )}
          {!hideGame && (
            <div className="col-span-2 hidden desktop-sm:block">
              <div className="text-p-2 text-neutral-blue-200">Game</div>
            </div>
          )}
          {!hideCarClass && (
            <div className="col-span-2 hidden desktop-sm:block">
              <div className="text-p-2 text-neutral-blue-200">Car class</div>
            </div>
          )}
          {!hideDays && (
            <div className="col-span-2 hidden desktop-sm:block">
              <div className="text-p-2 text-neutral-blue-200">Days</div>
            </div>
          )}

          <div className="col-span-4 hidden desktop-sm:block">
            <div className="text-p-2 text-neutral-blue-200">Keyword</div>
          </div>
          <div className="col-span-2 hidden desktop-sm:block" />

          {!hidePlatforms && (
            <div className="w-full desktop-sm:col-span-2">
              <div className="mb-1 text-p-2 font-semibold text-neutral-blue-200 desktop-sm:hidden">
                Platform
              </div>
              {searchOptions[0] ? (
                <SearchElement
                  {...searchOptions[0]}
                  value={filter['platforms']}
                  setValue={(newValue) => updateFilter('platforms', newValue)}
                  labelKey="nicename"
                  valueKey="name"
                  isMulti={true}
                />
              ) : (
                <div className="flex h-12 justify-between rounded-md bg-white px-4 py-3 desktop-xs:h-[57px] desktop-xs:py-4">
                  <p className="mt-px text-p-2 font-semibold text-neutral-blue-900">
                    Any
                  </p>
                  <Loader size="sm" />
                </div>
              )}
            </div>
          )}
          {!hideGame && (
            <div className="w-full desktop-sm:col-span-2">
              <div className="mb-1 text-p-2 text-neutral-blue-200 desktop-sm:hidden">
                Game
              </div>
              {searchOptions[1] ? (
                <SearchElement
                  {...searchOptions[1]}
                  value={filter['game']}
                  setValue={(newValue) => setGame('game', newValue)}
                  labelKey="nicename"
                  valueKey="name"
                  isMulti={false}
                />
              ) : (
                <div className="flex h-12 justify-between rounded-md bg-white px-4 py-3 desktop-xs:h-[57px] desktop-xs:py-4">
                  <p className="mt-px text-p-2 font-semibold text-neutral-blue-900">
                    Any
                  </p>
                  <Loader size="sm" />
                </div>
              )}
            </div>
          )}
          {!hideCarClass && (
            <div className="w-full desktop-sm:col-span-2">
              <div className="mb-1 text-p-2 text-neutral-blue-200 desktop-sm:hidden">
                Car class
              </div>

              {searchOptions[2] ? (
                <SearchElement
                  {...searchOptions[2]}
                  value={filter['carClasses']}
                  setValue={(newValue) => updateFilter('carClasses', newValue)}
                  labelKey="nicename"
                  valueKey="name"
                  isMulti
                />
              ) : (
                <div className="flex h-12 justify-between rounded-md bg-white px-4 py-3 desktop-xs:h-[57px] desktop-xs:py-4">
                  <p className="mt-px text-p-2 font-semibold text-neutral-blue-900">
                    Any
                  </p>
                  <Loader size="sm" />
                </div>
              )}
            </div>
          )}
          {!hideDays && (
            <div className="w-full desktop-sm:col-span-2">
              <div className="mb-1 text-p-2 text-neutral-blue-200 desktop-sm:hidden">
                Days
              </div>

              {searchOptions[3] ? (
                <SearchElement
                  {...searchOptions[3]}
                  value={filter['days']}
                  setValue={(newValue) => updateFilter('days', newValue)}
                  labelKey="nicename"
                  valueKey="name"
                  isMulti
                />
              ) : (
                <div className="flex h-12 justify-between rounded-md bg-white px-4 py-3 desktop-xs:h-[57px] desktop-xs:py-4">
                  <p className="mt-px text-p-2 font-semibold text-neutral-blue-900">
                    Any
                  </p>
                  <Loader size="sm" />
                </div>
              )}
            </div>
          )}
          <div className="flex w-full flex-col desktop-sm:col-span-4">
            <div className="mb-1 text-p-2 text-neutral-blue-200 desktop-sm:hidden">
              Keyword
            </div>

            <Input
              className="bg-shades-0! px-3! text-p-2! text-neutral-blue-900! placeholder-neutral-blue-500! hover:bg-[#dbdcdd]! focus:bg-shades-0! h-12 w-full rounded-md border border-neutral-blue-500 font-semibold transition-all duration-300 ease-in-out desktop-xs:h-[57px]"
              placeholder={textPlaceholder}
              onKeyUp={submitSearchOnEnterKeyPress}
              value={filter['keyword'] || ''}
              onChange={(e) =>
                setFilter({ ...filter, ['keyword']: e.target.value })
              }
              disabled={searching}
            />
          </div>

          <Button
            disabled={searching || !canRunSearch}
            className="bg-gf-gradient text-white! mt-2 flex h-[57px] w-full cursor-pointer items-center justify-center gap-1 rounded-md border-0 transition-all duration-300 ease-in-out hover:opacity-85 desktop-sm:col-span-2 desktop-sm:mt-0"
            onClick={() => search()}
          >
            {searching ? (
              <Loader
                size="sm"
                spinnerFillColor="shades-0"
                spinnerBgColor="primary"
              />
            ) : (
              <SearchIcon className="size-6 min-w-6 stroke-shades-0" />
            )}
            <p className="text-white! text-p-3 transition-all duration-300 ease-in-out">
              Search
            </p>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SearchHeader
